import { type ActionEvent } from "@hotwired/stimulus"

import DrawerController from "../drawer_controller"
import ModalController from "../modal_controller"
import SearchDateController from "../search/search_date_controller"
import SearchExperienceController from "../search/search_experience_controller"
import SearchGuestsController from "@/controllers/search/search_guests_controller"

import { saveNavigationTypeToStorage } from "@/helpers/ga4_helper"
import { setInputValueWithEvents } from "@/helpers/utils"

export default class extends ModalController {
  static outlets = ["drawer", "search-date", "search-experience", "search-guests"]
  declare readonly drawerOutlets: DrawerController[]
  declare readonly searchDateOutlet: SearchDateController
  declare readonly searchExperienceOutlet: SearchExperienceController
  declare readonly searchGuestsOutlet: SearchGuestsController

  static targets = [
    "calendarHeader",
    "calendarMonth",
    "dateFooter",
    "dateRadio",
    "experienceFooter",
    "experienceSummary",
    "footer",
    "guestsFooter",
    "guestsSummary",
    "locationFooter",
    "locationInput",
    "locationSummary"
  ]
  declare readonly calendarHeaderTarget: HTMLElement
  declare readonly calendarMonthTargets: HTMLTableElement[]
  declare readonly dateFooterTarget: HTMLElement
  declare readonly dateRadioTargets: HTMLInputElement[]
  declare readonly experienceFooterTarget: HTMLElement
  declare readonly experienceSummaryTarget: HTMLElement
  declare readonly footerTargets: HTMLElement[]
  declare readonly guestsFooterTarget: HTMLElement
  declare readonly guestsSummaryTarget: HTMLElement
  declare readonly locationFooterTarget: HTMLElement
  declare readonly locationInputTarget: HTMLInputElement
  declare readonly locationSummaryTarget: HTMLElement

  static values = {
    dateConfirmed: Boolean,
    experienceConfirmed: Boolean
  }

  declare dateConfirmedValue: boolean
  declare experienceConfirmedValue: boolean

  clearAll(): void {
    setInputValueWithEvents(this.locationInputTarget, "")
    this.searchDateOutlet.deselectAllDates()
    this.searchExperienceOutlet.resetExperience()
    this.searchGuestsOutlet.resetGuests()
    this.drawerOutlets.forEach((drawer) => drawer.close())
  }

  open(): void {
    super.open()

    // Set custom prop for calendar scroll offset equal to height of sticky header, plus gap.
    this.element.style.setProperty(
      "--search-modal-date-offset",
      `${this.calendarHeaderTarget.getBoundingClientRect().height + 16}px`
    )

    // Open first drawer that's missing a value.
    if (!this.currentOpenDrawer) {
      this.openNextEmptyDrawer()
    }
  }

  openDateDrawer(): void {
    // Select appropriate tab and scroll to selected date before opening drawer to reduce jumping.
    this.searchDateOutlet.selectCalendarTab()
    this.jumpToSelectedDate()
    this.openDrawer(this.dateDrawer, this.dateFooterTarget)
  }

  openExperienceDrawer(): void {
    this.openDrawer(this.experienceDrawer, this.experienceFooterTarget)
  }

  openGuestsDrawer(): void {
    this.openDrawer(this.guestsDrawer, this.guestsFooterTarget)
  }

  openLocationDrawer(): void {
    this.openDrawer(this.locationDrawer, this.locationFooterTarget, () => {
      this.locationInputTarget.focus()
    })
  }

  openNextEmptyDrawer(): void {
    const { currentOpenDrawer } = this

    const hasLocation = !!this.locationInputTarget.value
    const hasDate = this.searchDateOutlet.isDateSelected && this.dateConfirmedValue
    const hasExperience =
      !this.searchExperienceOutlet.isDefaultSelected && this.experienceConfirmedValue

    const followingDrawers = currentOpenDrawer
      ? this.drawerOutlets.filter(
          (drawer) =>
            currentOpenDrawer.element.compareDocumentPosition(drawer.element) &
            Node.DOCUMENT_POSITION_FOLLOWING
        )
      : this.drawerOutlets

    if (!hasLocation && followingDrawers.includes(this.locationDrawer)) {
      this.openLocationDrawer()
    } else if (!hasDate && followingDrawers.includes(this.dateDrawer)) {
      this.openDateDrawer()
    } else if (!hasExperience && followingDrawers.includes(this.experienceDrawer)) {
      this.openExperienceDrawer()
    } else {
      this.openGuestsDrawer()
    }
  }

  resetGuestsOrClearAll(): void {
    if (this.searchGuestsOutlet.isZeroSelected) {
      this.clearAll()
    } else {
      this.searchGuestsOutlet.resetGuests()
    }
  }

  unconfirmDate(): void {
    this.dateConfirmedValue = false
  }

  unconfirmExperience(): void {
    this.experienceConfirmedValue = false
  }

  confirmSelectedDate(): void {
    this.dateConfirmedValue = true
    this.openNextEmptyDrawer()
  }

  confirmSelectedExperience(): void {
    this.experienceConfirmedValue = true
    this.openNextEmptyDrawer()
  }

  setDateFooter(): void {
    const { primaryButton, secondaryLink } = this.footerButtonsFor(this.dateFooterTarget)

    if (this.searchDateOutlet.isDateSelected) {
      secondaryLink.textContent = "Reset arrival date"
      primaryButton.textContent = "Add date"
      primaryButton.disabled = false
    } else {
      secondaryLink.textContent = "Skip arrival date"
      primaryButton.textContent = "Select date"
      primaryButton.disabled = true
    }
  }

  setExperienceSummaryAndFooter({ params: { name } }: ActionEvent): void {
    const { secondaryLink } = this.footerButtonsFor(this.experienceFooterTarget)

    this.experienceSummaryTarget.textContent = name
    secondaryLink.hidden = this.searchExperienceOutlet.isDefaultSelected
    this.unconfirmExperience()
  }

  setGuestsSummaryAndFooter(): void {
    const { secondaryLink } = this.footerButtonsFor(this.guestsFooterTarget)

    this.guestsSummaryTarget.textContent = this.searchGuestsOutlet.summaryText

    if (this.searchGuestsOutlet.isZeroSelected) {
      secondaryLink.textContent = "Clear all"
    } else {
      secondaryLink.textContent = "Clear guests"
    }
  }

  setLocationSummary({ currentTarget }: ActionEvent): void {
    this.locationSummaryTarget.textContent = (currentTarget as HTMLInputElement).value || "Any"
  }

  skipLocation(): void {
    setInputValueWithEvents(this.locationInputTarget, "")
    this.openNextEmptyDrawer()
  }

  skipOrResetDate(): void {
    if (this.searchDateOutlet.isDateSelected) {
      this.searchDateOutlet.deselectAllDates()
    } else {
      this.openNextEmptyDrawer()
    }

    this.unconfirmDate()
  }

  skipOrResetExperience(): void {
    if (this.searchExperienceOutlet.isDefaultSelected) {
      this.openNextEmptyDrawer()
    } else {
      this.searchExperienceOutlet.resetExperience()
    }

    this.unconfirmExperience()
  }

  submit(): void {
    saveNavigationTypeToStorage("search_box")
  }

  private get currentOpenDrawer(): DrawerController | undefined {
    return this.drawerOutlets.find((drawer) => drawer.openValue)
  }

  private get dateDrawer(): DrawerController {
    return this.drawerFor("[data-controller*=search-date]")
  }

  private drawerFor(searchInputControllerSelector: string): DrawerController {
    return this.drawerOutlets.find(
      (drawer) =>
        !!drawer.element.querySelector(searchInputControllerSelector) ||
        !!drawer.element.closest(searchInputControllerSelector)
    )!
  }

  private get experienceDrawer(): DrawerController {
    return this.drawerFor("[data-controller*=search-experience]")
  }

  private footerButtonsFor(footerTarget: HTMLElement): {
    primaryButton: HTMLButtonElement
    secondaryLink: HTMLButtonElement
  } {
    return {
      primaryButton: footerTarget.querySelector<HTMLButtonElement>("button:last-of-type")!,
      secondaryLink: footerTarget.querySelector("button:first-of-type")!
    }
  }

  private get guestsDrawer(): DrawerController {
    return this.drawerFor("[data-controller*=search-guests]")
  }

  private jumpToSelectedDate(): void {
    const month = this.selectedDateRadio
      ? this.calendarMonthTargets.find((month) => month.contains(this.selectedDateRadio!))
      : this.calendarMonthTargets.at(0)
    const calendar = month!.parentElement!

    // We use `scrollTop` on parent instead of `scrollIntoView` on element because the latter
    // will try to scroll ancestor containers rather than the immediate parent, and we can't
    // hide overflow on those ancestors without breaking the auto drawer height that fills
    // the available modal space.
    calendar.scrollTop = month!.offsetTop - calendar.offsetTop
  }

  private get locationDrawer(): DrawerController {
    return this.drawerFor("[data-controller*=search-location]")
  }

  private openDrawer(
    drawer: DrawerController,
    footerTarget: HTMLElement,
    onOpen?: () => void
  ): void {
    // Set custom prop for drawer height offset equal to height of previous drawers, plus padding.
    const DRAWER_GAP = 16
    const PADDING = 8
    const drawerIndex = this.drawerOutlets.indexOf(drawer)
    const offset = drawer.element.getBoundingClientRect().height + DRAWER_GAP
    this.element.style.setProperty(
      "--search-modal-height-offset",
      `${offset * drawerIndex + PADDING}px`
    )

    drawer.open()
    drawer.afterTransition(() => {
      this.showFooterFor(footerTarget)
      onOpen?.()
    })
  }

  private get selectedDateRadio(): HTMLInputElement | undefined {
    return this.dateRadioTargets.find((radio) => radio.checked)
  }

  private showFooterFor(footerTarget: HTMLElement): void {
    this.footerTargets.forEach((footer) => (footer.hidden = footer !== footerTarget))
  }
}
